/**
 * Format-agnostic parsers: markdown/text via the chunker, JSON via defensive
 * shape-sniffing, plus the shared heading→epistemic keyword table. These are
 * both the "Somewhere else" lane and the substrate the platform parsers ride.
 */
import type { NamiEpistemic } from '../../types/nami.js';
import { chunkMarkdown, chunkPaste, cleanLine, packToLimit } from '../chunk.js';
import { IMPORT_ITEM_MAX_CHARS, type MemoryImportItem, type NamedText } from '../types.js';

const MIN_ITEM_CHARS = 8;

/** Ordered keyword families — first match wins (procedure before project_state: "workflow" contains "work"). */
const EPISTEMIC_FAMILIES: Array<[RegExp, NamiEpistemic]> = [
  [/prefer|style|taste|convention|settings?|likes?\b|dislikes?\b|always\b|never\b/i, 'preference'],
  [/identity|about me|who i am|profile|background|\bbio\b/i, 'identity'],
  [/people|team|contacts?|relationship|friends|family|colleagues?/i, 'relationship'],
  [/how to|how i\b|procedure|workflow|process|\brules?\b|\bsteps\b/i, 'procedure'],
  [/project|\bwork\b|working|status|current|in flight/i, 'project_state'],
];

export function epistemicForHeading(heading: string | null): NamiEpistemic | undefined {
  if (!heading) return undefined;
  for (const [re, epistemic] of EPISTEMIC_FAMILIES) {
    if (re.test(heading)) return epistemic;
  }
  return undefined;
}

function toIso(value: unknown): string | undefined {
  if (typeof value !== 'string' && typeof value !== 'number') return undefined;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
}

export function plainTextItems(
  text: string,
  base: { epistemic?: NamiEpistemic; observedAt?: string; sourceDetail: string },
): MemoryImportItem[] {
  const cleaned = cleanLine(text.replace(/\n+/g, ' '));
  if (cleaned.length < MIN_ITEM_CHARS) return [];
  return packToLimit(cleaned, IMPORT_ITEM_MAX_CHARS).map((chunk) => ({
    text: chunk,
    ...(base.epistemic ? { epistemic: base.epistemic } : {}),
    ...(base.observedAt ? { observedAt: base.observedAt } : {}),
    sourceDetail: base.sourceDetail,
  }));
}

const TEXT_KEYS = ['content', 'text', 'memory', 'summary', 'value'] as const;
const DATE_KEYS = ['created_at', 'createdAt', 'date'] as const;
const CATEGORY_KEYS = ['category', 'type', 'kind'] as const;

function entryToItems(entry: unknown, sourceDetail: string): MemoryImportItem[] {
  if (typeof entry === 'string') return plainTextItems(entry, { sourceDetail });
  if (typeof entry !== 'object' || entry === null) return [];
  const record = entry as Record<string, unknown>;
  const textKey = TEXT_KEYS.find((k) => typeof record[k] === 'string' && (record[k] as string).trim().length > 0);
  if (!textKey) return [];
  const dateKey = DATE_KEYS.find((k) => record[k] !== undefined);
  const categoryKey = CATEGORY_KEYS.find((k) => typeof record[k] === 'string');
  const observedAt = dateKey ? toIso(record[dateKey]) : undefined;
  const epistemic = categoryKey ? epistemicForHeading(record[categoryKey] as string) : undefined;
  return plainTextItems(record[textKey] as string, {
    sourceDetail,
    ...(observedAt ? { observedAt } : {}),
    ...(epistemic ? { epistemic } : {}),
  });
}

/** Defensive JSON memory reader: string arrays, {memories:[…]}, arrays of {content|text|memory|summary|value, …}. */
export function parseGenericJson(name: string, text: string): MemoryImportItem[] {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return [];
  }
  const list = Array.isArray(parsed)
    ? parsed
    : typeof parsed === 'object' && parsed !== null && Array.isArray((parsed as Record<string, unknown>).memories)
      ? ((parsed as Record<string, unknown>).memories as unknown[])
      : null;
  if (!list) return [];
  return list.flatMap((entry) => entryToItems(entry, basename(name)));
}

function basename(path: string): string {
  const parts = path.split('/');
  return parts[parts.length - 1] || path;
}

const MARKDOWN_EXTENSIONS = /\.(md|markdown|txt)$/i;

export function parseGenericFiles(files: NamedText[]): MemoryImportItem[] {
  const items: MemoryImportItem[] = [];
  for (const file of files) {
    if (MARKDOWN_EXTENSIONS.test(file.name)) {
      items.push(...chunkMarkdown(file.text, basename(file.name), { epistemicForHeading }));
    } else if (/\.json$/i.test(file.name)) {
      items.push(...parseGenericJson(file.name, file.text));
    }
  }
  return items;
}

export function parsePasted(text: string): MemoryImportItem[] {
  return chunkPaste(text, { epistemicForHeading });
}
